import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './useRedux';
import { loginAsync, logout, getMeAsync, clearError } from '@/store/authSlice';
import type { LoginRequest } from '@/api/authService';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, token, error } = useAppSelector((s) => s.auth);

  // Al montar: si hay token pero no user, validar con el backend
  useEffect(() => {
    if (token && !user && !isLoading) {
      dispatch(getMeAsync());
    }
  }, [token, user, isLoading, dispatch]);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      const result = await dispatch(loginAsync(credentials));
      if (loginAsync.fulfilled.match(result)) {
        navigate('/');
        return true;
      }
      return false;
    },
    [dispatch, navigate]
  );

  const handleLogout = useCallback(() => {
    dispatch(logout());
    navigate('/login');
  }, [dispatch, navigate]);

  const hasRole = useCallback(
    (role: string) => user?.roles?.includes(role) ?? false,
    [user]
  );

  const hasPermission = useCallback(
    (modulo: string, accion: string) => {
      if (!user) return false;
      if (user.roles.includes('Admin') || user.roles.includes('SuperAdmin')) return true;
      return user.roles.includes(`${modulo}:${accion}`);
    },
    [user]
  );

  const handleClearError = useCallback(() => dispatch(clearError()), [dispatch]);

  return {
    isAuthenticated,
    isLoading,
    user,
    token,
    error,
    login,
    logout: handleLogout,
    hasRole,
    hasPermission,
    clearError: handleClearError,
  };
};

export default useAuth;
